/*
 * @format
 */

import React from 'react';
import { Circle, G, Path, Rect, Text } from 'react-native-svg';

import { MEDIUM, REGULAR } from './DiagramParts';
import { accent, black, primary } from '../../colours';

/*
 * The watch's zone screen, drawn to scale — the shared subject of the Watch App
 * help illustrations. It is drawn rather than screenshotted so it stays readable
 * at any width and cannot drift out of date with the app it is describing.
 *
 * Everything here is ported from the watch's own ZoneDial.kt — the arc window
 * and geometry, the ring and marker sizes, the order and colours of the
 * readouts, the Wear typography roles — expressed in the watch's own dp/sp so
 * the two can be compared line by line. Keep them in step: a picture that draws
 * the face differently from the watch teaches the wrong thing.
 */
const ARC_START_DEG = 130;
const ARC_SWEEP_DEG = 280;
const TEMP_MIN_C = 5;
const TEMP_MAX_C = 30;

/*
 * The example zone: warmer than it has been asked to be, so the arc stops at the
 * target and the room marker sits beyond it. Exported because a topic told over
 * several steps draws the same zone on each of them — the reader is meant to be
 * looking at the zone they just tapped — and restating the numbers per diagram
 * lets the steps drift apart.
 */
export const EXAMPLE_ZONE = {
  name: 'Zone 1',
  targetC: 19.5,
  roomC: 21.5,
  mode: 'Auto',
};

const ZONE_NAME = EXAMPLE_ZONE.name;
const TARGET_C = EXAMPLE_ZONE.targetC;
const CURRENT_C = EXAMPLE_ZONE.roomC;
const MODE = EXAMPLE_ZONE.mode;

// The round face, and the watch's own units mapped onto it: the dial is drawn
// at the size a mid-range round Wear screen gives it, so every dp and sp below
// can be read straight off ZoneDial.kt and the Wear Material 3 type scale.
export const FACE_DIAMETER = 224;
export const FACE_RADIUS = FACE_DIAMETER / 2;

const WEAR_SCREEN_DP = 220;
const dp = value => (value * FACE_DIAMETER) / WEAR_SCREEN_DP;
const sp = dp;

const RING_STROKE = dp(4);
const CURRENT_DOT = dp(6);
const CURRENT_DOT_OUTLINE = dp(2);
const TARGET_DOT = dp(5);
// DialRing insets the track by enough to keep the room marker and its outline
// inside the screen.
export const RING_RADIUS =
  FACE_RADIUS - (CURRENT_DOT + CURRENT_DOT_OUTLINE + dp(3));

// The Wear Material 3 roles ZoneDial asks for, in the order they stack.
const NAME_SIZE = sp(14); // titleSmall
const NOW_SIZE = sp(12); // bodySmall
const NUMERAL_SIZE = sp(40); // numeralMedium
const MODE_SIZE = sp(15); // labelMedium

// The readouts are laid out as one vertically-centred block, which leaves the
// setpoint sitting on the ring's centre with the name and room temperature
// above it and the mode pill below. Offsets are from the face's centre.
const NAME_DY = -dp(52);
const NOW_DY = -dp(30);
const NUMERAL_DY = dp(14);
const PILL_DY = dp(34);
const PILL_HEIGHT = dp(30);
const PILL_WIDTH = dp(60);
const MODE_DY = PILL_DY + dp(20);

// The zone pager's indicator, which sits at the bottom edge of the screen — in
// the gap the 280° track leaves for it.
const PAGE_DOT = dp(3);
const PAGE_DOT_DY = RING_RADIUS + dp(4);
const PAGE_DOT_GAP = dp(4);

const ACCENT_GREEN = accent(400); // arc, target marker, mode pill
const NEUTRAL_WHITE = primary(50); // setpoint, room marker
const DIM_GREY = primary(300); // zone name, room temperature
const TRACK_GREY = primary(900); // the unfilled track
const MODE_PILL_ALPHA = 0.14;
const NOW_LABEL_ALPHA = 0.5;

/*
 * The room-temperature line is two colours — the watch dims the word "now" and
 * keeps the reading itself at the secondary tone — which means two text nodes,
 * and two text nodes cannot be centred by textAnchor: each would centre on its
 * own. So the pair is positioned by hand, which needs the width of the string.
 *
 * SVG offers no way to measure text, so these are the advance widths, in ems,
 * of the glyphs this one line uses, read out of the Montserrat-Regular in
 * app/assets/fonts. Montserrat's digits are proportional — a "1" is barely half
 * the width of a "0" — so a per-character table it has to be.
 */
const MONTSERRAT_ADVANCE = {
  ' ': 0.262,
  '.': 0.212,
  '°': 0.419,
  0: 0.662,
  1: 0.361,
  2: 0.568,
  3: 0.564,
  4: 0.661,
  5: 0.566,
  6: 0.609,
  7: 0.589,
  8: 0.638,
  9: 0.609,
  n: 0.677,
  o: 0.627,
  w: 0.879,
};

// An unlisted glyph falls back to a middling width: the line lands a shade off
// centre rather than not at all.
const widthOf = (text, fontSize) =>
  [...text].reduce((total, char) => total + (MONTSERRAT_ADVANCE[char] ?? 0.6), 0) *
  fontSize;

const NOW_LABEL = 'now';
const NOW_VALUE = `${CURRENT_C}°`;
const NOW_LINE_WIDTH = widthOf(`${NOW_LABEL} ${NOW_VALUE}`, NOW_SIZE);
// The space between the two is never drawn — it is only the gap the value is
// pushed along by — so no renderer can trim it away.
const NOW_LABEL_DX = -NOW_LINE_WIDTH / 2;
const NOW_VALUE_DX = NOW_LABEL_DX + widthOf(`${NOW_LABEL} `, NOW_SIZE);

const fractionFor = celsius =>
  Math.min(Math.max((celsius - TEMP_MIN_C) / (TEMP_MAX_C - TEMP_MIN_C), 0), 1);

const degreesFor = celsius =>
  ARC_START_DEG + fractionFor(celsius) * ARC_SWEEP_DEG;

const pointAt = (cx, cy, radius, degrees) => {
  const radians = (degrees * Math.PI) / 180;

  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
};

const arcPath = (cx, cy, startDeg, endDeg) => {
  const start = pointAt(cx, cy, RING_RADIUS, startDeg);
  const end = pointAt(cx, cy, RING_RADIUS, endDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;

  return `M ${start.x} ${start.y} A ${RING_RADIUS} ${RING_RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`;
};

// The arc stops at whichever of the room and the target is lower, so it never
// runs past the target marker — here the target, the room being the warmer.
const ARC_END_C = Math.min(TARGET_C, CURRENT_C);

/*
 * The setpoint numeral's own box. This is exactly the tap target ZoneDial makes
 * of it — tapTarget() adds no padding, so the doorway to the temperature editor
 * is the drawn numeral and nothing more; the name and the room-temperature line
 * above it are outside it.
 *
 * The digits stand on the baseline, so the box runs a cap height up from there.
 */
const NUMERAL_CAP = 0.7;

export const setpointBoxFor = (cx, cy) => {
  const width = widthOf(`${TARGET_C}°`, NUMERAL_SIZE);
  const height = NUMERAL_SIZE * NUMERAL_CAP;

  return { x: cx - width / 2, y: cy + NUMERAL_DY - height, width, height };
};

/* The mode pill's box, for annotations that need to point at or cover it. */
export const modePillFor = (cx, cy) => ({
  x: cx - PILL_WIDTH / 2,
  y: cy + PILL_DY,
  width: PILL_WIDTH,
  height: PILL_HEIGHT,
});

/* Where a caption's leader should land on each readout. */
export const anchorsFor = (cx, cy) => ({
  roomTemperature: { x: cx + NOW_LABEL_DX - 8, y: cy + NOW_DY - 4 },
  setpoint: { x: cx - 22, y: cy + NUMERAL_DY + 8 },
  mode: { x: cx + PILL_WIDTH / 2 + 7, y: cy + PILL_DY + PILL_HEIGHT / 2 },
});

function WatchFace({ cx, cy }) {
  const targetMarker = pointAt(cx, cy, RING_RADIUS, degreesFor(TARGET_C));
  const currentMarker = pointAt(cx, cy, RING_RADIUS, degreesFor(CURRENT_C));

  return (
    <G>
      <Circle cx={cx} cy={cy} r={FACE_RADIUS} fill={black} />

      {/* The unfilled track, then the reached portion of it. */}
      <Path
        d={arcPath(cx, cy, ARC_START_DEG, ARC_START_DEG + ARC_SWEEP_DEG)}
        stroke={TRACK_GREY}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        fill="none"
      />
      <Path
        d={arcPath(cx, cy, ARC_START_DEG, degreesFor(ARC_END_C))}
        stroke={ACCENT_GREEN}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        fill="none"
      />
      {/* Room marker first, then the target on top, as the watch draws them. */}
      <Circle
        cx={currentMarker.x}
        cy={currentMarker.y}
        r={CURRENT_DOT}
        fill={NEUTRAL_WHITE}
        stroke={black}
        strokeWidth={CURRENT_DOT_OUTLINE}
      />
      <Circle
        cx={targetMarker.x}
        cy={targetMarker.y}
        r={TARGET_DOT}
        fill={ACCENT_GREEN}
      />

      <Text
        x={cx}
        y={cy + NAME_DY}
        fill={DIM_GREY}
        fontFamily={MEDIUM}
        fontSize={NAME_SIZE}
        textAnchor="middle">
        {ZONE_NAME}
      </Text>

      {/* Only the "now" label is dimmed; the reading keeps the secondary tone.
          Laid out from the left of the measured pair, so the line as a whole is
          centred on the numeral below. */}
      <Text
        x={cx + NOW_LABEL_DX}
        y={cy + NOW_DY}
        fill={DIM_GREY}
        fillOpacity={NOW_LABEL_ALPHA}
        fontFamily={REGULAR}
        fontSize={NOW_SIZE}>
        {NOW_LABEL}
      </Text>
      <Text
        x={cx + NOW_VALUE_DX}
        y={cy + NOW_DY}
        fill={DIM_GREY}
        fontFamily={REGULAR}
        fontSize={NOW_SIZE}>
        {NOW_VALUE}
      </Text>

      <Text
        x={cx}
        y={cy + NUMERAL_DY}
        fill={NEUTRAL_WHITE}
        fontFamily={MEDIUM}
        fontSize={NUMERAL_SIZE}
        textAnchor="middle">
        {`${TARGET_C}°`}
      </Text>

      <Rect
        x={cx - PILL_WIDTH / 2}
        y={cy + PILL_DY}
        width={PILL_WIDTH}
        height={PILL_HEIGHT}
        rx={PILL_HEIGHT / 2}
        fill={ACCENT_GREEN}
        fillOpacity={MODE_PILL_ALPHA}
      />
      <Text
        x={cx}
        y={cy + MODE_DY}
        fill={ACCENT_GREEN}
        fontFamily={MEDIUM}
        fontSize={MODE_SIZE}
        textAnchor="middle">
        {MODE}
      </Text>

      <Circle
        cx={cx - PAGE_DOT - PAGE_DOT_GAP / 2}
        cy={cy + PAGE_DOT_DY}
        r={PAGE_DOT}
        fill={NEUTRAL_WHITE}
      />
      <Circle
        cx={cx + PAGE_DOT + PAGE_DOT_GAP / 2}
        cy={cy + PAGE_DOT_DY}
        r={PAGE_DOT}
        fill={TRACK_GREY}
      />
    </G>
  );
}

export default WatchFace;
